import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getEstimates, updateEstimate, computeQboNameForJobOrderWindow, computeQboNameForAdder } from '../../services/estimate-service';
import { createJobOrder, updateJobOrder, getJobOrderByEstimate, deleteJobOrder, GLASS_TYPES, COLOR_OPTIONS } from '../../services/job-order-service';
import { generateJobOrderPDF } from '../../services/job-order-pdf'
import PDFPreviewModal from '../../components/PDFPreviewModal';
import TrimOrderModal from '../../components/TrimOrderModal';
import OrderedWindowsShopOrderModal from '../../components/OrderedWindowsShopOrderModal';
import { useAuth } from '../../contexts/AuthContext';
import { useConfirm } from '../../components/ConfirmDialog';
import AppLayout from '../../components/AppLayout';
import Button from '../../components/ui/Button';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import { ArrowLeft, AlertTriangle } from 'lucide-react';

// ── Palette — same warm, coherent family as the rest of Estimates.
const INK    = '#2B2620';
const WALNUT = '#8B4513';
const MOSS   = '#3B6B44';
const OCHRE  = '#A6752A';
const RUST   = '#A13D2D';
const STONE  = '#8C8275';
const PAPER  = '#FDFBF8';

const FONT_DISPLAY = "'Fraunces', Georgia, serif";
const FONT_SANS    = "'IBM Plex Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

// TWP window type codes + labels
const TWP_TYPES = [
  { value: 'DH',   label: 'Double Hung' },
  { value: 'HS',   label: 'Horizontal Slider' },
  { value: 'CSMT', label: 'Casement' },
  { value: 'PW',   label: 'Picture Window' },
  { value: 'BSMT', label: 'Basement Hopper' },
  { value: '3HS',  label: '3-Lite Slider' },
  { value: 'BM',   label: 'Brickmould' },
];

// Maps TWP pricing windowType string → TWP type code used in job order
const PRICING_TYPE_TO_TWP = {
  'Double Hung':    'DH',
  'Slider':         'HS',
  '3 Lite Slider':  '3HS',
  'Picture Window': 'PW',
  'Casement':       'CSMT',
  'Awning':         'CSMT',
  'Basement Hopper':'BSMT',
};

const TYPE_CONFIGS = {
  DH:    ['Single', '2-Lite', '3-Lite', '4-Lite', '5-Lite', 'Q/H/Q'],
  HS:    ['Single', '2-Lite', '3-Lite', '4-Lite', '5-Lite', 'Q/H/Q'],
  '3HS': ['Single'],
  PW:    ['Single', '2-Lite', '3-Lite', '4-Lite', '5-Lite'],
  BSMT:  ['Single', '2-Lite', '3-Lite', '4-Lite', '5-Lite'],
  CSMT:  ['Single', '2-Lite', '3-Lite', '4-Lite', '5-Lite', 'Q/H/Q'],
  BM:    ['Single'],
};

const GLASS_PKGS  = ['DC', 'DCA', 'DLA', 'TLA', 'TLK'];
const HANDING_OPT = ['Hinge Left', 'Hinge Right'];

const STYLE_TO_TWP = {
  'double hung': 'DH', 'horizontal slider': 'HS', 'casement': 'CSMT',
  'picture window': 'PW', 'basement hopper': 'BSMT', '3-lite slider': '3HS', 'brickmould': 'BM',
};
function guessType(style) {
  const d = (style || '').toLowerCase();
  for (const [k, v] of Object.entries(STYLE_TO_TWP)) { if (d.includes(k)) return v; }
  return 'DH';
}

function LBL({ children }) {
  return <div style={{ fontSize:'10px', fontWeight:'700', color:'#555', marginBottom:'4px', textTransform:'uppercase', letterSpacing:'0.05em' }}>{children}</div>;
}
function Row({ children, cols }) {
  return <div style={{ display:'grid', gridTemplateColumns: cols || `repeat(${React.Children.count(children)}, 1fr)`, gap:'10px', alignItems:'start' }}>{children}</div>;
}
function Chip({ label, active, onClick }) {
  return (
    <button type="button" onClick={onClick} style={{
      padding:'5px 12px', borderRadius:'16px', fontSize:'11.5px', fontWeight: active ? '700' : '500',
      border: `1.5px solid ${active ? WALNUT : '#ddd'}`,
      background: active ? WALNUT : 'white', color: active ? 'white' : '#555',
      cursor:'pointer', fontFamily:'inherit', whiteSpace:'nowrap',
    }}>{label}</button>
  );
}

function TWPWindowFields({ w, idx, upd }) {
  const twpType = w.twpType || guessType(w.style || w.description || '');
  const configs = TYPE_CONFIGS[twpType] || ['Single'];
  const isCSMT  = twpType === 'CSMT';
  const multiConfig = configs.length > 1;

  // Defense: legacy/migrated data may have twpConfig as an OBJECT instead of a string.
  // Coerce to a sane string so the UI never displays "[object Object]".
  const twpConfigStr = (typeof w.twpConfig === 'string' && w.twpConfig)
    ? w.twpConfig
    : 'Single';

  function set(field, val) { upd(idx, field, val); }
  function setType(val) { set('twpType', val); if (!TYPE_CONFIGS[val]?.includes(twpConfigStr)) set('twpConfig', 'Single'); }

  const selStyle = { width:'100%', padding:'7px 10px', borderRadius:'8px', border:'1px solid #e0e0e0', fontSize:'12px', fontFamily:'inherit', background:'white', cursor:'pointer' };

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:'12px' }}>
      <Row cols="1fr 1fr 1fr 1fr">
        <div>
          <LBL>Outside Color</LBL>
          <div style={{ padding:'7px 10px', borderRadius:'8px', border:'1px solid #e0e0e0', fontSize:'12px', background:'#f9f9f9', color:'#555', fontWeight:'600' }}>White</div>
        </div>
        <div>
          <LBL>Inside Color</LBL>
          <select value={w.colorInside||'White'} onChange={e => set('colorInside', e.target.value)} style={{ width:'100%', padding:'7px 10px', borderRadius:'8px', border:'1px solid #e0e0e0', fontSize:'12px', fontFamily:'inherit', background:'white', cursor:'pointer' }}>
            <option value="White">White</option>
            <option value="Dk Woodgrain/White">Dk Woodgrain / White</option>
          </select>
        </div>
        <div>
          <LBL>Window Type</LBL>
          <select value={twpType} onChange={e => setType(e.target.value)} style={selStyle}>
            {TWP_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>
        <div>
          <LBL>Glass Package</LBL>
          <select value={w.glassPkg||'DLA'} onChange={e => set('glassPkg',e.target.value)} style={selStyle}>
            {GLASS_PKGS.map(g => <option key={g} value={g}>{g}</option>)}
          </select>
        </div>
      </Row>
      {(multiConfig || isCSMT) && (
        <div style={{ display:'flex', gap:'24px', flexWrap:'wrap' }}>
          {multiConfig && (
            <div>
              <LBL>Configuration</LBL>
              <div style={{ display:'flex', gap:'6px', flexWrap:'wrap', marginTop:'2px' }}>
                {configs.map(c => <Chip key={c} label={c} active={twpConfigStr===c} onClick={() => set('twpConfig',c)} />)}
              </div>
            </div>
          )}
          {isCSMT && (
            <div>
              <LBL>Handing</LBL>
              <div style={{ display:'flex', gap:'6px', marginTop:'2px' }}>
                {HANDING_OPT.map(h => <Chip key={h} label={h} active={(w.handing||'Hinge Left')===h} onClick={() => set('handing',h)} />)}
              </div>
            </div>
          )}
        </div>
      )}
      <div>
        <LBL>Flanges &amp; Grids</LBL>
        <div style={{ display:'flex', gap:'10px', flexWrap:'wrap' }}>
          <CB label="Flanges"  checked={!!w.flanges}      onChange={v => set('flanges',v)} />
          <CB label="Colonial" checked={!!w.gridColonial}  onChange={v => set('gridColonial',v)} />
          <CB label="Diamond"  checked={!!w.gridDiamond}   onChange={v => set('gridDiamond',v)} />
          <CB label="Prairie"  checked={!!w.gridPrairie}   onChange={v => set('gridPrairie',v)} />
        </div>
      </div>
      <div>
        <LBL>Screen</LBL>
        <div style={{ display:'flex', gap:'10px', flexWrap:'wrap' }}>
          <CB label="Half Screen" checked={!!w.screenHalf} onChange={v => { set('screenHalf',v); if(v) set('screenFull',false); }} />
          <CB label="Full Screen" checked={!!w.screenFull} onChange={v => { set('screenFull',v); if(v) set('screenHalf',false); }} />
        </div>
      </div>
      <div>
        <LBL>Install Notes / Extras</LBL>
        <input name={`twp-jo-extras-${idx}`} autoComplete="off" value={w.extrasNotes||''} onChange={e => set('extrasNotes',e.target.value)}
          placeholder="Special notes, vendor info..."
          style={{ width:'100%', padding:'7px 10px', borderRadius:'8px', border:'1px solid #e5e5e5', fontSize:'12px', fontFamily:'inherit', boxSizing:'border-box' }} />
      </div>
    </div>
  );
}

const WINDOW_STYLES = [
  'Double Hung','Single Hung','Casement','Awning','Slider',
  'Picture','Bay','Bow','Garden','Hopper','Transom','Jalousie',
  'Egress','Fixed','Tilt','Gliding','Single Casement','Double Casement',
  'Triple Casement','Quad Casement','DH','HS','CSMT','PW','BSMT','3HS','BM'
];

const ADDER_TYPES_LIST = [
  'inside trim','outside trim','brick mold','coving','rework opening',
  'remove whole frame','remove rotten seals','trash removal','screen repair',
  'glass repair','shipping','delivery','mileage','setup','miscellaneous',
  'misc','service call','permit','tax','trim out','trim'
];

function isWindowType(w) {
  if (w.lineType === 'twp_window') return true;
  if (w.lineType === 'ordered_window' || w.lineType === 'door' || w.lineType === 'adder') return false;
  const desc = (w.style || w.description || '').toLowerCase();
  if (['door','patio door','storm door','exterior door','sliding door','side lite'].some(d => desc.includes(d))) return false;
  if (ADDER_TYPES_LIST.some(a => desc.includes(a.toLowerCase()))) return false;
  if (WINDOW_STYLES.some(s => desc.includes(s.toLowerCase()))) return true;
  if (w.width && w.height) return true;
  return false;
}

function isAdderLine(w) {
  if (w.lineType === 'adder' || w.type === 'adder') return true;
  return false;
}

const CB = ({ label, checked, onChange }) => (
  <label style={{ display:'flex', alignItems:'center', gap:'5px', cursor:'pointer', fontSize:'12px', fontWeight:'600', color: checked ? MOSS : '#333', whiteSpace:'nowrap' }}>
    <input type="checkbox" checked={!!checked} onChange={e => onChange(e.target.checked)} style={{ width:'15px', height:'15px', accentColor: MOSS, cursor:'pointer' }} />
    {label}
  </label>
);

const SEL = ({ value, onChange, options, placeholder }) => (
  <select value={value||''} onChange={e => onChange(e.target.value)}
    style={{ width:'100%', padding:'6px 8px', borderRadius:'7px', border:'1px solid #e0e0e0', fontSize:'12px', fontFamily:'inherit', background:'white', color: value ? '#2b2b2b' : '#555' }}>
    <option value="">{placeholder||'— Select —'}</option>
    {options.map(o => <option key={o} value={o}>{o}</option>)}
  </select>
);

const ADDER_LIST = [
  'Window Installation','Inside Trim','Outside Trim','Brickmold','Coving',
  'Rework Opening','Remove Whole Frame','Remove Rotten Sills','Trash Removal',
  'Screen Repair','Glass Repair','Shipping / Delivery','Mileage',
  'Setup / Misc. Fee','Service Call',
];

export default function CreateJobOrderPage() {
  const navigate = useNavigate();
  const { estimateId } = useParams();
  const { currentUser } = useAuth();

  const [estimate, setEstimate]           = useState(null);
  const confirm = useConfirm();
  const [contractNumber, setContractNumber] = useState('');
  const [windows, setWindows]             = useState([]);
  const [saving, setSaving]               = useState(false);
  const [previewBlob, setPreviewBlob]     = useState(null);
  const [showContractPrompt, setShowContractPrompt] = useState(false);
  const [pendingContractNumber, setPendingContractNumber] = useState('');
  const [showTrimOrder, setShowTrimOrder] = useState(false);
  const [showOrderedShopOrder, setShowOrderedShopOrder] = useState(false);
  const [existingJO, setExistingJO]       = useState(null);
  const [loading, setLoading]             = useState(true);
  const [howDidYouHear, setHowDidYouHear] = useState('');
  const [orderDate, setOrderDate]         = useState((() => { const n = new Date(); return `${n.getFullYear()}-${String(n.getMonth()+1).padStart(2,'0')}-${String(n.getDate()).padStart(2,'0')}`; })());
  const [casingWraps, setCasingWraps]                = useState(false);
  const [casingWrapsColor, setCasingWrapsColor]      = useState('');
  const [adders, setAdders]               = useState([]);

  const [global, setGlobal] = useState({
    colorOutside:'', colorInside:'', glass:'',
    flanges:false, gridColonial:false, gridDiamond:false, gridPrairie:false,
    screenHalf:false, screenFull:false,
  });
  const [applyGlobal, setApplyGlobal] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [estimateId]);

  async function load() {
    setLoading(true);
    const all = await getEstimates();
    const est = all.find(e => e.id === estimateId);
    if (!est) { navigate('/estimates'); return; }
    setEstimate(est);

    const existing = await getJobOrderByEstimate(estimateId);
    if (existing) {
      setExistingJO(existing);
      if (existing.contractNumber) setContractNumber(existing.contractNumber);
      if (existing.howDidYouHear)  setHowDidYouHear(existing.howDidYouHear);
      if (existing.orderDate)      setOrderDate(existing.orderDate);
      if (existing.casingWraps !== undefined) setCasingWraps(existing.casingWraps);
      if (existing.casingWrapsColor !== undefined) setCasingWrapsColor(existing.casingWrapsColor);
    }

    const baseLines = (est.lines || []).filter(l => l.type !== 'note');

    const allWins = (existing?.windows && existing.windows.length > 0)
      ? existing.windows.map((ew, i) => {
          const line = baseLines[ew.lineIndex] || baseLines[i] || {};
          return {
            ...line,
            lineIndex:       ew.lineIndex ?? i,
            lineType:        ew.lineType || line.type || '',
            qty:             ew.qty          || line.qty || 1,
            width:           ew.width        || line.width || '',
            height:          ew.height       || line.height || '',
            description:     ew.description  || line.description || '',
            room:            ew.room         || line.room || '',
            unitPrice:       ew.unitPrice    || line.price || 0,
            style:           ew.style        || line.description || '',
            colorOutside:    ew.colorOutside ?? '',
            colorInside:     ew.colorInside  ?? '',
            glass:           ew.glass        ?? '',
            flanges:         ew.flanges      ?? false,
            gridColonial:    ew.gridColonial ?? false,
            gridDiamond:     ew.gridDiamond  ?? false,
            gridPrairie:     ew.gridPrairie  ?? false,
            screenHalf:      ew.screenHalf   ?? false,
            screenFull:      ew.screenFull   ?? false,
            extrasNotes:     ew.extrasNotes  ?? '',
            vendor:          ew.vendor       ?? '',
            // ── Mulled-unit metadata — must survive save/reload so the
            //    Trim Order can group panes back into single units.
            isMulledPane:    ew.isMulledPane    ?? false,
            mulledUnitLabel: ew.mulledUnitLabel ?? '',
            mulledPaneLabel: ew.mulledPaneLabel ?? '',
            mulledUnitIndex: ew.mulledUnitIndex ?? null,
            mulledUnitTotal: ew.mulledUnitTotal ?? null,
            // Window/Door/Ordered classification fields
            twpType:         ew.twpType   ?? line.twpType   ?? '',
            twpConfig:       ew.twpConfig ?? 'Single',
            handing:         ew.handing   ?? 'Hinge Left',
            glassPkg:        ew.glassPkg  ?? 'DLA',
          };
        })
      : (() => {
          // Build from estimate lines — explode mulled units into individual pane rows
          const result = [];
          baseLines.forEach((line, i) => {
            if (isAdderLine(line)) return;

            if (line.isMulledUnit) {
              // Parse back the JSON-stringified arrays that were saved to Firestore
              let cutlistWindows = [];
              try {
                cutlistWindows = line.cutlistWindowsJson
                  ? JSON.parse(line.cutlistWindowsJson)
                  : (Array.isArray(line.cutlistWindows) ? line.cutlistWindows : []);
              } catch(e) {
                cutlistWindows = Array.isArray(line.cutlistWindows) ? line.cutlistWindows : [];
              }

              if (cutlistWindows.length > 0) {
                // ── Mulled unit: one card per pane ──────────────────────────
                // The grand total (windows + mulls + install, after multiplier)
                // is split proportionally across panes based on each pane's
                // individual window price weight.
                // QuickBooks sees only the individual windows with their share
                // of the total — mulls and install are baked in, never visible.

                const grandTotal = parseFloat(line.price || line.unitPrice || 0);

                // Sum of raw individual pane prices (used for proportion)
                const rawPaneTotal = cutlistWindows.reduce((s, p) => s + (parseFloat(p.price) || 0), 0);

                // Calculate each pane's proportional share of the grand total
                // If we can't price a pane (out of range), split equally
                const paneShares = cutlistWindows.map((pane) => {
                  if (rawPaneTotal <= 0) {
                    // Equal split fallback
                    return parseFloat((grandTotal / cutlistWindows.length).toFixed(2));
                  }
                  const rawPrice = parseFloat(pane.price) || 0;
                  return parseFloat(((rawPrice / rawPaneTotal) * grandTotal).toFixed(2));
                });

                // Fix rounding: last pane absorbs any cent difference
                const sharesSum = paneShares.reduce((s, v) => s + v, 0);
                const diff = parseFloat((grandTotal - sharesSum).toFixed(2));
                if (paneShares.length > 0) paneShares[paneShares.length - 1] = parseFloat((paneShares[paneShares.length - 1] + diff).toFixed(2));

                const lineQty = parseInt(line.qty) || 1;
                for (let unitIdx = 0; unitIdx < lineQty; unitIdx++) {
                  cutlistWindows.forEach((pane, paneIdx) => {
                    const basePaneLabel = pane.mullLabel || `Pane ${paneIdx + 1}`;
                    const paneLabel = lineQty > 1 ? `Unit ${unitIdx + 1} — ${basePaneLabel}` : basePaneLabel;
                    const isCasement = pane.windowType === 'Casement';
                    const paneHingeSide = pane.hingeSide === 'right' ? 'Hinge Right' : 'Hinge Left';
                    const hingeNote = isCasement ? ` (${paneHingeSide})` : '';
                    result.push({
                      lineIndex:       i,
                      lineType:        'twp_window',
                      type:            'twp_window',
                      qty:             1,
                      width:           String(pane.width  || ''),
                      height:          String(pane.height || ''),
                      description:     `${line.description || 'Mulled Unit'} — ${paneLabel}${hingeNote}`,
                      room:            line.room || '',
                      unitPrice:       paneShares[paneIdx],
                      style:           pane.windowType || '',
                      twpType:         PRICING_TYPE_TO_TWP[pane.windowType] || 'DH',
                      twpConfig:       'Single',
                      handing:         paneHingeSide,
                      glassPkg:        'DLA',
                      colorOutside:    '',
                      colorInside:     '',
                      glass:           '',
                      flanges:         false,
                      gridColonial:    false,
                      gridDiamond:     false,
                      gridPrairie:     false,
                      screenHalf:      false,
                      screenFull:      false,
                      extrasNotes:     '',
                      vendor:          '',
                      isMulledPane:    true,
                      mulledUnitLabel: line.description || 'Mulled Unit',
                      mulledPaneLabel: basePaneLabel,
                      mulledUnitIndex: unitIdx + 1,
                      mulledUnitTotal: lineQty,
                    });
                  });
                }
              } else {
                // cutlistWindows missing — fall back to single line
                // Same fix as the regular single path: force twpConfig to a string.
                const fbTwpConfigObj = (line.twpConfig && typeof line.twpConfig === 'object') ? line.twpConfig : null;
                result.push({
                  ...line,
                  lineIndex: i, lineType: line.type || '',
                  colorOutside: '', colorInside: fbTwpConfigObj?.variant || '', glass: '',
                  flanges: false, gridColonial: false, gridDiamond: false,
                  gridPrairie: false, screenHalf: false, screenFull: false,
                  extrasNotes: '', vendor: '',
                  style: line.description || '', unitPrice: line.price || 0,
                  twpConfig: 'Single',
                  twpType:   fbTwpConfigObj?.windowType
                              ? (PRICING_TYPE_TO_TWP[fbTwpConfigObj.windowType] || undefined)
                              : undefined,
                });
              }
            } else {
              // ── Regular single window / door / ordered line ──────────────
              // IMPORTANT: line.twpConfig from QuickQuote is an OBJECT
              // ({ windowType, variant, glassConfig, ... }) but the JobOrder UI
              // expects a STRING ('Single', '2-wide', etc.) — so we extract
              // what we need into separate fields and force twpConfig to 'Single'.
              const lineTwpConfigObj = (line.twpConfig && typeof line.twpConfig === 'object') ? line.twpConfig : null;
              result.push({
                ...line,
                lineIndex:    i,
                lineType:     line.type || '',
                colorOutside: '',
                colorInside:  lineTwpConfigObj?.variant || '',
                glass:        '',
                flanges:      false,
                gridColonial: false,
                gridDiamond:  false,
                gridPrairie:  false,
                screenHalf:   false,
                screenFull:   false,
                extrasNotes:  '',
                vendor:       '',
                style:        line.description || '',
                unitPrice:    line.price || 0,
                // Override the OBJECT twpConfig from estimate with a STRING default
                twpConfig:    'Single',
                // If we have a windowType from the estimate, map it to a TWP type code
                twpType:      lineTwpConfigObj?.windowType
                                ? (PRICING_TYPE_TO_TWP[lineTwpConfigObj.windowType] || undefined)
                                : undefined,
              });
            }
          });
          return result;
        })();

    // Backfill qboName on every window. Saved values take priority; the
    // helper recomputes from twpType / colorInside / handing / twpConfig only
    // when no qboName is set. The result travels through save → Cloud Function.
    const finalWins = Array.isArray(allWins)
      ? allWins
          .filter(w => !isAdderLine(w))
          .map(w => ({ ...w, qboName: w.qboName || computeQboNameForJobOrderWindow(w) || null }))
      : [];
    setWindows(finalWins);

    // Load adders
    if (existing?.adders && existing.adders.length > 0) {
      setAdders(existing.adders.map((a, i) => ({
        id: a.id || Date.now() + i,
        name: a.name || a.description || '',
        qty: a.qty || 1,
        price: a.price !== undefined ? a.price : '',
        notes: a.notes || '',
      })));
    } else {
      const estAdders = (est.lines || []).filter(l => l.type === 'adder');
      setAdders(estAdders.map((a, i) => ({
        id: Date.now() + i,
        name: a.description || '',
        qty: a.qty || 1,
        price: a.price !== undefined ? a.price : '',
        notes: '',
      })));
    }

    setLoading(false);
  }

  function upd(idx, field, value) {
    setWindows(prev => prev.map((w, i) => i === idx ? { ...w, [field]: value } : w));
  }

  function applyGlobalToAll() {
    setWindows(prev => prev.map(w => isWindowType(w) ? { ...w, ...global } : w));
  }

  function addAdder(name) { setAdders(prev => [...prev, { id: Date.now(), name, qty: 1, price: '', notes: '' }]); }
  function updAdder(id, field, val) { setAdders(prev => prev.map(a => a.id === id ? { ...a, [field]: val } : a)); }
  function removeAdder(id) { setAdders(prev => prev.filter(a => a.id !== id)); }

  async function handleDelete() {
    if (!existingJO) return;
    if (!(await confirm({ title: 'Delete this job order?', message: 'This will also remove the calendar job, cutlist job, and all PDFs. This cannot be undone.', tone: 'danger', confirmText: 'Delete' }))) return;
    setSaving(true);
    try {
      await deleteJobOrder(existingJO.id);
      navigate('/estimates');
    } catch(e) { alert('Error deleting job order: ' + e.message); }
    setSaving(false);
  }

  async function handleSave(contractOverride) {
    // contractOverride lets the popup pass the value directly,
    // bypassing the async setContractNumber() race.
    const cnum = (contractOverride !== undefined ? contractOverride : contractNumber);
    if (!String(cnum || '').trim()) { alert('Please enter a contract number'); return; }
    setSaving(true);

    // Helper: replace undefined/NaN with a safe fallback for Firestore
    const safe = (v, fallback = '') => (v === undefined || v === null || (typeof v === 'number' && isNaN(v))) ? fallback : v;
    const safeBool = (v) => !!v;
    const safeNum  = (v, fallback = 0) => { const n = parseFloat(v); return isNaN(n) ? fallback : n; };

    try {
      const payload = {
        contractNumber: safe(cnum),
        howDidYouHear:  safe(howDidYouHear),
        orderDate:      safe(orderDate),
        casingWraps:      safeBool(casingWraps),
        casingWrapsColor: casingWraps ? (casingWrapsColor || '').trim() : '',
        subtotal:       safeNum(estimate?.subtotal),
        taxEnabled:     safeBool(estimate?.taxEnabled),
        taxRate:        safe(estimate?.taxRate, '2.041'),
        taxType:        safe(estimate?.taxType, !estimate?.taxEnabled ? 'none' : estimate?.taxRate === '6.2' ? 'sales' : 'excise'),
        taxAmount:      safeNum(estimate?.taxAmount),
        grandTotal:     safeNum(estimate?.grandTotal),
        adders: adders.map(a => ({
          id:          safe(a.id, Date.now()),
          name:        safe(a.name),
          description: safe(a.name),
          qty:         parseInt(a.qty) || 1,
          price:       a.price !== '' ? safeNum(a.price) : 0,
          notes:       safe(a.notes),
          qboName:     a.qboName || computeQboNameForAdder(a) || null,
        })),
        windows: windows.map((w) => ({
          lineIndex:       safe(w.lineIndex, 0),
          lineType:        safe(w.lineType),
          type:            safe(w.lineType || w.type),
          qty:             safe(w.qty, 1),
          width:           safe(w.width),
          height:          safe(w.height),
          description:     safe(w.description),
          room:            safe(w.room),
          unitPrice:       safeNum(w.unitPrice ?? w.price),
          style:           safe(w.style || w.description),
          colorOutside:    safe(w.colorOutside),
          colorInside:     safe(w.colorInside),
          glass:           safe(w.glass),
          flanges:         safeBool(w.flanges),
          gridColonial:    safeBool(w.gridColonial),
          gridDiamond:     safeBool(w.gridDiamond),
          gridPrairie:     safeBool(w.gridPrairie),
          screenHalf:      safeBool(w.screenHalf),
          screenFull:      safeBool(w.screenFull),
          extrasNotes:     safe(w.extrasNotes),
          twpType:         safe(w.twpType),
          twpConfig:       safe(w.twpConfig, 'Single'),
          handing:         safe(w.handing, 'Hinge Left'),
          glassPkg:        safe(w.glassPkg, 'DLA'),
          vendor:          safe(w.vendor),
          isMulledPane:    safeBool(w.isMulledPane),
          mulledUnitLabel: safe(w.mulledUnitLabel),
          mulledPaneLabel: safe(w.mulledPaneLabel),
          mulledUnitIndex: w.mulledUnitIndex ?? null,
          mulledUnitTotal: w.mulledUnitTotal ?? null,
          qboName:         w.qboName || computeQboNameForJobOrderWindow(w) || null,
        })),
      };

      if (existingJO) {
        await updateJobOrder(existingJO.id, payload);
        const refreshed = await getJobOrderByEstimate(estimateId);
        setExistingJO(refreshed);
      } else {
        const joRef = await createJobOrder(
          estimateId,
          { ...estimate, windows: payload.windows },
          cnum,
          currentUser?.email
        );
        await updateJobOrder(joRef.id, payload);
        await updateEstimate(estimateId, { jobOrderCreated: true, contractNumber: safe(cnum) });
        const jo = await getJobOrderByEstimate(estimateId);
        setExistingJO(jo);
      }
      alert('Job Order saved!');
    } catch (e) {
      console.error(e);
      alert('Error saving: ' + e.message);
    }
    setSaving(false);
  }

  const ui   = (w) => { const ww = parseFloat(w.width)||0, hh = parseFloat(w.height)||0; return ww && hh ? ww + hh : (ww||hh||''); };
  const size = (w) => w.width && w.height ? `${w.width}" × ${w.height}"` : (w.width ? `${w.width}"` : w.height ? `${w.height}"` : '');

  const c = estimate?.customer || {};
  const inputS = { padding:'6px 10px', borderRadius:'8px', border:'1px solid #e0e0e0', fontSize:'12px', fontFamily:'inherit', background:'white' };

  const breadcrumb = 'Dashboard › Estimates › Job Order';

  return (
    <AppLayout
      title={existingJO ? 'Update Job Order' : 'Create Job Order'}
      breadcrumb={breadcrumb}
      topBarActions={
        loading ? null : (
          <>
            {existingJO && (
              <Button variant="secondary" size="small" onClick={async () => { const blob = await generateJobOrderPDF({...existingJO, howDidYouHear, orderDate, casingWraps, casingWrapsColor, windows, adders}, true); setPreviewBlob(blob); }}>
                PDF
              </Button>
            )}
            {existingJO && <Button variant="secondary" size="small" onClick={() => setShowTrimOrder(true)}>Trim Order</Button>}
            {existingJO && <Button variant="secondary" size="small" onClick={() => setShowOrderedShopOrder(true)}>Ordered Products</Button>}
            {existingJO && <Button variant="secondary" size="small" onClick={handleDelete} disabled={saving}>Delete</Button>}
            <Button size="small" onClick={() => setShowContractPrompt(true)} disabled={saving}>
              {saving ? 'Saving...' : existingJO ? 'Update Job Order' : 'Save Job Order'}
            </Button>
          </>
        )
      }
    >
    {loading ? (
      <div style={{ textAlign: 'center', padding: '100px 20px' }}>
        <LoadingSpinner size="medium" color={WALNUT} />
      </div>
    ) : (
    <div style={{ fontFamily: FONT_SANS }}>

      {/* Back — this page can be reached from several parents (Estimates
          list, Customer Hub, Quick Quote), so go back to wherever the user
          actually came from rather than a fixed destination. */}
      <button
        onClick={() => navigate(-1)}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: '5px',
          background: 'none', border: 'none', color: STONE,
          fontSize: '12px', fontWeight: '600', cursor: 'pointer',
          fontFamily: FONT_SANS, padding: '4px 0', marginBottom: '10px',
        }}
        onMouseEnter={e => e.currentTarget.style.color = WALNUT}
        onMouseLeave={e => e.currentTarget.style.color = STONE}
      >
        <ArrowLeft size={13} strokeWidth={2} /> Back
      </button>

      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '18px' }}>
        <span style={{ fontSize: '12px', color: STONE }}>{c.name}</span>
        {(c.locationLabel || c.address) && (
          <>
            <span style={{ width: '3px', height: '3px', borderRadius: '50%', background: '#d8d0c6' }} />
            <span style={{ fontSize: '12px', color: STONE }}>{c.locationLabel || c.address}</span>
          </>
        )}
      </div>

      <div>

        {existingJO && (
          <div style={{ background: '#FBF1E0', border: '1px solid #f0ddb8', borderRadius:'10px',
            padding:'10px 18px', marginBottom:'16px', display:'flex', alignItems:'center', gap:'10px' }}>
            <div style={{ fontSize:'13px', color: OCHRE, fontFamily: FONT_SANS }}>
              <strong>Job Order Already Created</strong> — Contract #{existingJO.contractNumber}. Editing notifies the office manager.
            </div>
          </div>
        )}

        {/* Customer card — clean horizontal */}
        <div style={{ background: PAPER, borderRadius:'10px', border:'0.5px solid #e8e2da',
          padding:'18px 22px', marginBottom:'14px', display:'flex', alignItems:'center', gap:'24px' }}>
          <div style={{ width:'44px', height:'44px', borderRadius:'50%', background:'#f5e6d3',
            display:'flex', alignItems:'center', justifyContent:'center',
            fontFamily: FONT_DISPLAY, fontSize:'15px', fontWeight:'600', color:WALNUT, flexShrink:0 }}>
            {(c.name || 'C').split(' ').map(s => s[0]).filter(Boolean).slice(0,2).join('').toUpperCase()}
          </div>
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ fontSize:'16px', fontWeight:'600', color: INK, letterSpacing:'-0.2px' }}>{c.name}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', fontSize:'12px', color: STONE, marginTop:'4px' }}>
              <span>{[c.address, c.city, c.state, c.zip].filter(Boolean).join(', ')}</span>
              {c.phone     && <span><span style={{ fontWeight:'600' }}>Home</span> {c.phone}</span>}
              {c.cellPhone && <span><span style={{ fontWeight:'600' }}>Cell</span> {c.cellPhone}</span>}
              {c.workPhone && <span><span style={{ fontWeight:'600' }}>Work</span> {c.workPhone}</span>}
            </div>
          </div>
          <div style={{ display:'flex', flexDirection:'column', alignItems:'flex-end', gap:'4px' }}>
            <div style={{ fontSize:'10px', color:'#555', letterSpacing:'0.5px', fontWeight:'600' }}>ORDER DATE</div>
            <input type="date" value={orderDate} onChange={e => setOrderDate(e.target.value)}
              style={{ fontSize:'13px', border:'0.5px solid #d4d0c8', borderRadius:'6px', padding:'5px 10px', fontFamily:'inherit' }} />
          </div>
        </div>

        {/* Apply to all */}
        <div style={{ background:'white', borderRadius:'10px', border:'0.5px solid #e8e2da',
          padding:'16px 20px', marginBottom:'14px' }}>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'12px' }}>
            <div style={{ fontSize:'11px', fontWeight:'600', color:'#555', letterSpacing:'0.5px', textTransform:'uppercase' }}>Apply to all windows</div>
            <CB label="Apply to all" checked={applyGlobal} onChange={setApplyGlobal} />
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(140px, 1fr))', gap:'10px', alignItems:'end' }}>
            <div><div style={{ fontSize:'10px', fontWeight:'700', color:'#555', marginBottom:'3px' }}>OUTSIDE COLOR</div><SEL value={global.colorOutside} onChange={v => setGlobal(p=>({...p, colorOutside:v}))} options={COLOR_OPTIONS} /></div>
            <div><div style={{ fontSize:'10px', fontWeight:'700', color:'#555', marginBottom:'3px' }}>INSIDE COLOR</div><SEL value={global.colorInside} onChange={v => setGlobal(p=>({...p, colorInside:v}))} options={COLOR_OPTIONS} /></div>
            <div><div style={{ fontSize:'10px', fontWeight:'700', color:'#555', marginBottom:'3px' }}>GLASS</div><SEL value={global.glass} onChange={v => setGlobal(p=>({...p, glass:v}))} options={GLASS_TYPES} /></div>
            <div><div style={{ fontSize:'10px', fontWeight:'700', color:'#555', marginBottom:'6px' }}>FLANGES</div><CB label="Flanges" checked={global.flanges} onChange={v => setGlobal(p=>({...p, flanges:v}))} /></div>
            <div>
              <div style={{ fontSize:'10px', fontWeight:'700', color:'#555', marginBottom:'6px' }}>GRIDS</div>
              <div style={{ display:'flex', gap:'8px', flexWrap:'wrap' }}>
                <CB label="Colonial" checked={global.gridColonial} onChange={v => setGlobal(p=>({...p, gridColonial:v}))} />
                <CB label="Diamond"  checked={global.gridDiamond}  onChange={v => setGlobal(p=>({...p, gridDiamond:v}))} />
                <CB label="Prairie"  checked={global.gridPrairie}  onChange={v => setGlobal(p=>({...p, gridPrairie:v}))} />
              </div>
            </div>
            <div>
              <div style={{ fontSize:'10px', fontWeight:'700', color:'#555', marginBottom:'6px' }}>SCREEN</div>
              <div style={{ display:'flex', gap:'8px' }}>
                <CB label="1/2"  checked={global.screenHalf} onChange={v => setGlobal(p=>({...p, screenHalf:v}))} />
                <CB label="Full" checked={global.screenFull} onChange={v => setGlobal(p=>({...p, screenFull:v}))} />
              </div>
            </div>
          </div>
          {applyGlobal && (
            <button onClick={applyGlobalToAll} style={{ marginTop:'12px', background:MOSS, color:'white', border:'none', borderRadius:'7px', padding:'8px 18px', fontSize:'13px', fontWeight:'600', cursor:'pointer', fontFamily:'inherit' }}>
              Apply to All Windows Now
            </button>
          )}
        </div>

        {/* Windows & Doors */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize:'13px', fontWeight:'600', color: INK,
          marginBottom:'10px', padding:'0 4px', fontFamily: FONT_SANS }}>
          <span>Windows &amp; Doors</span>
          <span style={{ width: '3px', height: '3px', borderRadius: '50%', background: '#d8d0c6' }} />
          <span style={{ fontWeight: 500, color: STONE }}>{windows.length} {windows.length === 1 ? 'item' : 'items'}</span>
        </div>

        {windows.map((w, idx) => {
          // STRICT classification: only real twp_window goes to cutlist.
          // Notes are filtered out upstream, so they never reach here.
          const isMulledPane = w.isMulledPane;
          const isTwpWindow  = w.lineType === 'twp_window' && !isMulledPane;
          const isDoor       = w.lineType === 'door';
          const isWin        = isTwpWindow;

          const cardColor = isMulledPane ? '#7c3aed'
                          : isTwpWindow  ? WALNUT
                          : isDoor       ? '#1565C0'
                          : '#6A1B9A';
          const cardLabel = isMulledPane ? 'MULLED PANE · CUTLIST'
                          : isTwpWindow  ? 'TWP · CUTLIST'
                          : isDoor       ? 'DOOR · NOTES'
                          : 'ORDERED · NOTES';

          const uiVal = ui(w);
          const szStr = size(w);
          const unitP = parseFloat(w.unitPrice||w.price)||0;
          const total = unitP * (parseFloat(w.qty)||1);
          const desc  = w.style || w.description || '';

          return (
            <div key={idx} style={{ background: isMulledPane ? '#faf7ff' : 'white',
              borderRadius:'10px', border:'0.5px solid #e8e2da', borderLeft:`3px solid ${cardColor}`,
              marginBottom:'10px', overflow:'hidden' }}>
              <div style={{ padding:'12px 18px', display:'flex', alignItems:'center', gap:'12px', flexWrap:'wrap' }}>
                <div style={{ width:'22px', height:'22px', borderRadius:'50%', background: cardColor+'22', color: cardColor, display:'flex', alignItems:'center', justifyContent:'center', fontSize:'11px', fontWeight:'600', flexShrink:0 }}>{idx+1}</div>
                <div style={{ flex:1, display:'flex', gap:'16px', alignItems:'center', flexWrap:'wrap' }}>
                  <div style={{ fontWeight:'600', fontSize:'14px', color:'#1a1a1a' }}>{desc}</div>
                  <div style={{ fontSize:'10px', fontWeight:'600', color: cardColor, background: cardColor+'18', padding:'3px 9px', borderRadius:'5px', letterSpacing:'0.4px' }}>{cardLabel}</div>
                  <div style={{
                    display:'flex', alignItems:'center', gap:'4px',
                    fontSize:'10px', fontWeight:'600',
                    color:      w.qboName ? MOSS : RUST,
                    background: w.qboName ? '#EAF1EA' : '#FBEBE8',
                    border:    '1px solid ' + (w.qboName ? '#cfe3d0' : '#f0d2cb'),
                    padding:'3px 9px', borderRadius:'5px',
                  }} title={w.qboName ? `Will sync as QBO item: ${w.qboName}` : 'No QBO Product set — line will sync as generic "TWP Services". Fix by setting QBO Product on the estimate row.'}>
                    {!w.qboName && <AlertTriangle size={10} strokeWidth={2} />}
                    QBO: {w.qboName || 'missing'}
                  </div>
                  {/* Mulled unit pane badge */}
                  {isMulledPane && (
                    <div style={{ fontSize:'10px', fontWeight:'700', color:'#7c3aed', background:'rgba(124,58,237,0.08)', border:'1px solid rgba(124,58,237,0.2)', padding:'2px 8px', borderRadius:'20px' }}>
                      {w.mulledUnitLabel} · {w.mulledPaneLabel}
                    </div>
                  )}
                  {szStr && <div style={{ fontSize:'12px', color:'#555', fontVariantNumeric:'tabular-nums' }}>{szStr}</div>}
                  {uiVal && <div style={{ fontSize:'11px', color:'#555' }}>UI {uiVal}</div>}
                  <div style={{ fontSize:'11px', color:'#555' }}>Qty {w.qty||1}</div>
                  {w.room && <div style={{ fontSize:'11px', color:'#555' }}>{w.room}</div>}
                </div>
                {total > 0 && <div style={{ fontSize:'14px', fontWeight:'500', color:WALNUT, fontVariantNumeric:'tabular-nums' }}>${total.toLocaleString('en-US',{minimumFractionDigits:2})}</div>}
              </div>

              <div style={{ padding:'14px 18px' }}>
                {(isWin || isMulledPane) ? (
                  <TWPWindowFields w={w} idx={idx} upd={upd} />
                ) : (
                  <div style={{ display:'flex', flexDirection:'column', gap:'10px' }}>
                    <div style={{ display:'flex', gap:'10px', flexWrap:'wrap' }}>
                      <div style={{ flex:1, minWidth:'140px' }}>
                        <div style={{ fontSize:'10px', fontWeight:'700', color:'#555', marginBottom:'4px' }}>OUTSIDE COLOR</div>
                        <input name={`twp-jo-color-outside-${idx}`} autoComplete="off" value={w.colorOutside||''} onChange={e => upd(idx,'colorOutside',e.target.value)} placeholder="e.g. White, Bronze, Clay..." style={{ width:'100%', padding:'9px 12px', borderRadius:'8px', border:'1px solid #e5e5e5', fontSize:'13px', fontFamily:'inherit', boxSizing:'border-box' }} />
                      </div>
                      <div style={{ flex:1, minWidth:'140px' }}>
                        <div style={{ fontSize:'10px', fontWeight:'700', color:'#555', marginBottom:'4px' }}>INSIDE COLOR</div>
                        <input name={`twp-jo-color-inside-${idx}`} autoComplete="off" value={w.colorInside||''} onChange={e => upd(idx,'colorInside',e.target.value)} placeholder="e.g. White, Almond, Woodgrain..." style={{ width:'100%', padding:'9px 12px', borderRadius:'8px', border:'1px solid #e5e5e5', fontSize:'13px', fontFamily:'inherit', boxSizing:'border-box' }} />
                      </div>
                    </div>
                    {/* Flanges & Grids */}
                    <div>
                      <div style={{ fontSize:'10px', fontWeight:'700', color:'#555', marginBottom:'6px' }}>FLANGES &amp; GRIDS</div>
                      <div style={{ display:'flex', gap:'14px', flexWrap:'wrap' }}>
                        <label style={{ display:'flex', alignItems:'center', gap:'6px', cursor:'pointer', fontSize:'13px', color:'#2b2b2b', fontWeight:'500' }}>
                          <input type="checkbox" checked={!!w.flanges} onChange={e => upd(idx,'flanges',e.target.checked)} /> Flanges
                        </label>
                        <label style={{ display:'flex', alignItems:'center', gap:'6px', cursor:'pointer', fontSize:'13px', color:'#2b2b2b', fontWeight:'500' }}>
                          <input type="checkbox" checked={!!w.gridColonial} onChange={e => upd(idx,'gridColonial',e.target.checked)} /> Colonial
                        </label>
                        <label style={{ display:'flex', alignItems:'center', gap:'6px', cursor:'pointer', fontSize:'13px', color:'#2b2b2b', fontWeight:'500' }}>
                          <input type="checkbox" checked={!!w.gridDiamond} onChange={e => upd(idx,'gridDiamond',e.target.checked)} /> Diamond
                        </label>
                        <label style={{ display:'flex', alignItems:'center', gap:'6px', cursor:'pointer', fontSize:'13px', color:'#2b2b2b', fontWeight:'500' }}>
                          <input type="checkbox" checked={!!w.gridPrairie} onChange={e => upd(idx,'gridPrairie',e.target.checked)} /> Prairie
                        </label>
                      </div>
                    </div>
                    {/* Screen */}
                    <div>
                      <div style={{ fontSize:'10px', fontWeight:'700', color:'#555', marginBottom:'6px' }}>SCREEN</div>
                      <div style={{ display:'flex', gap:'14px', flexWrap:'wrap' }}>
                        <label style={{ display:'flex', alignItems:'center', gap:'6px', cursor:'pointer', fontSize:'13px', color:'#2b2b2b', fontWeight:'500' }}>
                          <input type="checkbox" checked={!!w.screenHalf}
                            onChange={e => { upd(idx,'screenHalf',e.target.checked); if(e.target.checked) upd(idx,'screenFull',false); }} /> Half Screen
                        </label>
                        <label style={{ display:'flex', alignItems:'center', gap:'6px', cursor:'pointer', fontSize:'13px', color:'#2b2b2b', fontWeight:'500' }}>
                          <input type="checkbox" checked={!!w.screenFull}
                            onChange={e => { upd(idx,'screenFull',e.target.checked); if(e.target.checked) upd(idx,'screenHalf',false); }} /> Full Screen
                        </label>
                      </div>
                    </div>
                    {/* Install Notes / Extras */}
                    <div>
                      <div style={{ fontSize:'10px', fontWeight:'700', color:'#555', marginBottom:'4px' }}>INSTALL NOTES / EXTRAS</div>
                      <input name={`twp-jo-extras-main-${idx}`} autoComplete="off" value={w.extrasNotes||''} onChange={e => upd(idx,'extrasNotes',e.target.value)}
                        placeholder="Special install requirements, hardware, finish..."
                        style={{ width:'100%', padding:'9px 12px', borderRadius:'8px', border:'1px solid #e5e5e5', fontSize:'13px', fontFamily:'inherit', boxSizing:'border-box' }} />
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}

        {/* Adders */}
        <div style={{ background:'white', borderRadius:'10px', border:'0.5px solid #e8e2da',
          padding:'18px 20px', marginBottom:'14px' }}>
          <div style={{ fontSize:'11px', fontWeight:'600', color:'#555', letterSpacing:'0.5px', marginBottom:'14px', textTransform:'uppercase' }}>Adders &amp; Extras</div>
          <div style={{ display:'flex', flexWrap:'wrap', gap:'6px', marginBottom:'14px' }}>
            {ADDER_LIST.map(name => (
              <button key={name} type="button" onClick={() => addAdder(name)} style={{ padding:'5px 12px', borderRadius:'16px', fontSize:'11px', fontWeight:'600', border:'1.5px solid #ddd', background:'#f9f9f9', color:'#555', cursor:'pointer', fontFamily:'inherit' }}>+ {name}</button>
            ))}
          </div>
          {adders.length > 0 && (
            <div style={{ display:'flex', flexDirection:'column', gap:'8px' }}>
              {adders.map(a => (
                <div key={a.id} style={{ display:'grid', gridTemplateColumns:'2fr 60px 100px 2fr auto', gap:'8px', alignItems:'center', background:'#fafafa', borderRadius:'10px', padding:'10px 12px', border:'1px solid #eee' }}>
                  <div>
                    <div style={{ fontSize:'9px', fontWeight:'700', color:'#555', marginBottom:'2px' }}>DESCRIPTION</div>
                    <input name={`twp-jo-adder-name-${a.id}`} autoComplete="off" value={a.name} onChange={e => updAdder(a.id,'name',e.target.value)} style={{ ...inputS, width:'100%', fontWeight:'600', color:WALNUT }} />
                  </div>
                  <div>
                    <div style={{ fontSize:'9px', fontWeight:'700', color:'#555', marginBottom:'2px' }}>QTY</div>
                    <input type="number" min="1" value={a.qty} onChange={e => updAdder(a.id,'qty',parseInt(e.target.value)||1)} style={{ ...inputS, width:'100%', textAlign:'center' }} />
                  </div>
                  <div>
                    <div style={{ fontSize:'9px', fontWeight:'700', color:'#555', marginBottom:'2px' }}>PRICE</div>
                    <input type="number" min="0" step="0.01" value={a.price} onChange={e => updAdder(a.id,'price',e.target.value)} placeholder="$0.00" style={{ ...inputS, width:'100%' }} />
                  </div>
                  <div>
                    <div style={{ fontSize:'9px', fontWeight:'700', color:'#555', marginBottom:'2px' }}>NOTES</div>
                    <input name={`twp-jo-adder-notes-${a.id}`} autoComplete="off" value={a.notes} onChange={e => updAdder(a.id,'notes',e.target.value)} placeholder="Optional notes" style={{ ...inputS, width:'100%' }} />
                  </div>
                  <button type="button" onClick={() => removeAdder(a.id)} style={{ background:'transparent', color:'#555', border:'none', borderRadius:'8px', padding:'6px 10px', cursor:'pointer', fontWeight:'400', fontSize:'18px', lineHeight:1 }}
                    onMouseEnter={e => e.currentTarget.style.color = '#dc2626'}
                    onMouseLeave={e => e.currentTarget.style.color = '#ccc'}>×</button>
                </div>
              ))}
            </div>
          )}
          {adders.length === 0 && <div style={{ fontSize:'12px', color:'#555', fontStyle:'italic' }}>No adders added. Click a button above to add.</div>}
        </div>

        {/* Tax + Totals */}
        {estimate && (
          <div style={{ background:'white', borderRadius:'10px', border:'0.5px solid #e8e2da', padding:'18px 22px' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline', marginBottom:'12px' }}>
              <div style={{ fontSize:'11px', fontWeight:'600', color:'#555', letterSpacing:'0.5px', textTransform:'uppercase' }}>Tax</div>
              <div style={{ fontSize:'11px', color:'#555' }}>From estimate · edit there if changes needed</div>
            </div>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-end', paddingTop:'14px', borderTop:'0.5px solid #e8e2da' }}>
              <div style={{ display:'flex', flexDirection:'column', gap:'8px' }}>
                <div style={{ padding:'5px 12px', borderRadius:'5px', background: WALNUT+'18', color: WALNUT, fontSize:'12px', fontWeight:'600', alignSelf:'flex-start' }}>
                  {!estimate.taxEnabled ? 'No Tax' : estimate.taxRate === '6.2' ? 'Sales Tax · 6.2%' : 'SD Excise Tax · 2.041%'}
                </div>
                <div style={{ display:'flex', gap:'18px', fontSize:'12px', color:'#555', fontVariantNumeric:'tabular-nums' }}>
                  <span>Subtotal <span style={{ color:'#1a1a1a', fontWeight:'500', marginLeft:'4px' }}>${(estimate.subtotal||0).toFixed(2)}</span></span>
                  {estimate.taxEnabled && <span>Tax <span style={{ color:'#1a1a1a', fontWeight:'500', marginLeft:'4px' }}>${(estimate.taxAmount||0).toFixed(2)}</span></span>}
                </div>
              </div>
              <div style={{ textAlign:'right' }}>
                <div style={{ fontSize:'10px', color:'#555', letterSpacing:'0.6px', fontWeight:'600' }}>GRAND TOTAL</div>
                <div style={{ fontSize:'30px', fontWeight:'600', color:WALNUT, lineHeight:1, letterSpacing:'-0.5px', fontVariantNumeric:'tabular-nums', marginTop:'4px' }}>
                  ${(estimate.grandTotal||0).toFixed(2)}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Bottom section */}
        <div style={{ background:'white', borderRadius:'10px', border:'0.5px solid #e8e2da', padding:'18px 22px', marginTop:'14px' }}>
          <div style={{ display:'grid', gridTemplateColumns:'1fr auto auto', gap:'24px', alignItems:'center' }}>
            <div>
              <div style={{ fontSize:'10px', fontWeight:'600', color:'#555', marginBottom:'6px', letterSpacing:'0.5px' }}>HOW DID YOU FIND OUT ABOUT US?</div>
              <input name="twp-jo-how-heard" autoComplete="off" value={howDidYouHear} onChange={e => setHowDidYouHear(e.target.value)} placeholder="Referral, Google, sign, etc."
                style={{ width:'100%', padding:'8px 12px', borderRadius:'8px', border:'1px solid #e5e5e5', fontSize:'13px', fontFamily:'inherit', boxSizing:'border-box' }} />
            </div>
            <div>
              <div style={{ fontSize:'10px', fontWeight:'600', color:'#555', marginBottom:'8px', letterSpacing:'0.5px' }}>CASING WRAPS</div>
              <div style={{ display:'flex', gap:'16px', alignItems:'center', flexWrap:'wrap' }}>
                <CB label="Yes" checked={casingWraps===true}  onChange={v => setCasingWraps(v ? true : false)} />
                <CB label="No"  checked={casingWraps===false} onChange={v => setCasingWraps(v ? false : true)} />
                {casingWraps && (
                  <input
                    name="twp-jo-casing-wraps-color"
                    autoComplete="off"
                    value={casingWrapsColor}
                    onChange={e => setCasingWrapsColor(e.target.value)}
                    placeholder="Color (e.g. White, Black, Almond)"
                    style={{
                      flex: '1', minWidth: '160px',
                      padding: '7px 11px',
                      borderRadius: '8px',
                      border: '1px solid #e5e5e5',
                      fontSize: '13px',
                      fontFamily: 'inherit',
                      boxSizing: 'border-box',
                    }}
                  />
                )}
              </div>
            </div>
            <div>
              <div style={{ fontSize:'10px', fontWeight:'600', color:'#555', marginBottom:'6px', letterSpacing:'0.5px' }}>ORDER DATE</div>
              <input type="date" value={orderDate} onChange={e => setOrderDate(e.target.value)} style={{ fontSize:'13px', border:'1px solid #e0e0e0', borderRadius:'8px', padding:'6px 10px', fontFamily:'inherit' }} />
            </div>
          </div>
        </div>

        {/* Save buttons */}
        <div style={{ display:'flex', gap:'10px', justifyContent:'center', padding:'24px 0' }}>
          {existingJO && <button onClick={handleDelete} disabled={saving}
            style={{ background:'transparent', color:'#c44', border:'0.5px solid #fcc', borderRadius:'8px', padding:'12px 24px', fontSize:'14px', fontWeight:'500', cursor:'pointer', fontFamily:'inherit' }}>
            Delete Job Order
          </button>}
          <button onClick={() => setShowContractPrompt(true)} disabled={saving}
            style={{ background:WALNUT, color:'white', border:'none', borderRadius:'8px', padding:'12px 32px', fontSize:'14px', fontWeight:'600', cursor:'pointer', fontFamily:'inherit' }}>
            {saving ? 'Saving...' : existingJO ? 'Update Job Order' : 'Save Job Order'}
          </button>
          {existingJO && (
            <button onClick={async () => { const blob = await generateJobOrderPDF({...existingJO, howDidYouHear, orderDate, casingWraps, casingWrapsColor, windows, adders}, true); setPreviewBlob(blob); }}
              style={{ background:'transparent', color: MOSS, border:`0.5px solid ${MOSS}66`, borderRadius:'8px', padding:'12px 24px', fontSize:'14px', fontWeight:'500', cursor:'pointer', fontFamily:'inherit' }}>
              Preview / Download PDF
            </button>
          )}
        </div>
      </div>

      {previewBlob && (
        <PDFPreviewModal pdfBlob={previewBlob} filename={`JobOrder_${contractNumber||'DRAFT'}_${(estimate?.customer?.name||'Customer').replace(/\s+/g,'_')}.pdf`} onClose={() => setPreviewBlob(null)} landscape />
      )}
      {showTrimOrder && (
        <TrimOrderModal jobOrder={{...existingJO, windows}} estimate={estimate} onClose={() => setShowTrimOrder(false)} onPreview={(blob) => { setPreviewBlob(blob); setShowTrimOrder(false); }} />
      )}
      {showOrderedShopOrder && (
        <OrderedWindowsShopOrderModal jobOrder={{...existingJO, windows, adders, customer: estimate?.customer || existingJO?.customer}} estimate={estimate} onClose={() => setShowOrderedShopOrder(false)} onPreview={(blob) => { setPreviewBlob(blob); setShowOrderedShopOrder(false); }} />
      )}

      {/* Contract Number Popup — gates the save action */}
      {showContractPrompt && (
        <div style={{ position:'fixed', inset:0, zIndex:10000, background:'rgba(255,255,255,0.15)',
          backdropFilter:'blur(8px)', display:'flex', alignItems:'center', justifyContent:'center', padding:'20px' }}
          onClick={e => e.target === e.currentTarget && setShowContractPrompt(false)}>
          <div style={{ background:'white', borderRadius:'14px', maxWidth:'400px', width:'100%',
            padding:'24px 26px', boxShadow:'0 20px 60px rgba(0,0,0,0.18)', border:'0.5px solid #e5e2db' }}>
            <div style={{ fontSize:'16px', fontWeight:'600', color:'#1a1a1a', marginBottom:'4px' }}>
              {existingJO ? 'Confirm Contract Number' : 'Contract / Job Number'}
            </div>
            <div style={{ fontSize:'12px', color:'#555', marginBottom:'16px', lineHeight:1.5 }}>
              {existingJO
                ? 'Confirm or update the contract number before saving.'
                : 'Required to save this job order. Used on all paperwork going forward.'}
            </div>
            <input
              type="text"
              name="twp-jo-contract-number"
              autoComplete="off"
              autoFocus
              value={pendingContractNumber || contractNumber}
              onChange={e => setPendingContractNumber(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  const v = (pendingContractNumber || contractNumber).trim();
                  if (!v) { alert('Please enter a contract number'); return; }
                  setContractNumber(v);
                  setShowContractPrompt(false);
                  // Pass v directly so handleSave doesn't depend on async state
                  handleSave(v);
                }
                if (e.key === 'Escape') { e.preventDefault(); setShowContractPrompt(false); }
              }}
              placeholder="e.g. 8889"
              maxLength={6}
              style={{ width:'100%', padding:'14px 16px', border:'0.5px solid #d4d0c8', borderRadius:'8px',
                fontSize:'24px', fontWeight:'600', color:WALNUT, fontFamily:'inherit', boxSizing:'border-box',
                fontVariantNumeric:'tabular-nums', textAlign:'center', outline:'none' }}
            />
            <div style={{ display:'flex', gap:'8px', marginTop:'16px' }}>
              <button onClick={() => setShowContractPrompt(false)}
                style={{ flex:1, background:'transparent', border:'0.5px solid #d4d0c8', padding:'10px',
                  borderRadius:'8px', fontSize:'13px', color:'#555', cursor:'pointer', fontFamily:'inherit' }}>
                Cancel
              </button>
              <button onClick={() => {
                  const v = (pendingContractNumber || contractNumber).trim();
                  if (!v) { alert('Please enter a contract number'); return; }
                  setContractNumber(v);
                  setShowContractPrompt(false);
                  // Pass v directly so handleSave doesn't depend on async state
                  handleSave(v);
                }}
                style={{ flex:2, background:WALNUT, border:'none', padding:'10px', borderRadius:'8px',
                  color:'white', fontSize:'13px', fontWeight:'600', cursor:'pointer', fontFamily:'inherit' }}>
                Save with this number
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    )}
    </AppLayout>
  );
}